import {
  LocationIcon,
  ServiceIcon,
  ChatIcon,
  TrophyIcon,
} from '@/components/icons'
import type { ContactInfo } from '@/entities'

interface ComingSoonPageProps {
  contactInfo?: ContactInfo | null
}

/**
 * Coming Soon page component with luxury styling to match the main website
 * Displays when maintenance mode is enabled, with dynamic contact information
 */
export default function ComingSoonPage({
  contactInfo,
}: ComingSoonPageProps = {}) {
  // Default contact info fallback
  const defaultContactInfo: ContactInfo = {
    companyName: 'Assets Infinity',
    description: 'Your trusted partner in Bangkok real estate',
    phone: ['+66(0)96 916 2959'],
    email: ['assetsinfinity2@gmail.com'],
    lineId: 'bella1noon',
  }

  const displayContactInfo = contactInfo || defaultContactInfo
  return (
    <div className="min-h-screen bg-luxury-gradient">
      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden hero">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-luxury-gold-dark/90"></div>
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 text-center hero-content">
          <div className="max-w-4xl px-4 mx-auto">
            {/* Main Heading */}
            <h1 className="mb-6 text-4xl font-bold text-white drop-shadow-2xl sm:text-5xl lg:text-6xl">
              Something Amazing is Coming Soon
            </h1>

            {/* Subtitle */}
            <p className="max-w-2xl mx-auto mb-8 text-lg leading-relaxed text-white/90 drop-shadow-lg sm:text-xl lg:text-2xl">
              We're crafting an exceptional property management experience. Our
              website is currently being enhanced to serve you better.
            </p>

            {/* Features Preview */}
            <div className="grid max-w-3xl grid-cols-1 gap-6 mx-auto mb-12 sm:grid-cols-3">
              <div className="p-6 border rounded-lg border-white/20 bg-white/10 backdrop-blur-sm">
                <div className="flex justify-center mb-4">
                  <LocationIcon className="w-8 h-8 text-luxury-gold" />
                </div>
                <h3 className="mb-2 font-semibold text-white">
                  Prime Locations
                </h3>
                <p className="text-sm text-white/80">
                  Premium properties in Bangkok's most desirable areas
                </p>
              </div>

              <div className="p-6 border rounded-lg border-white/20 bg-white/10 backdrop-blur-sm">
                <div className="flex justify-center mb-4">
                  <ServiceIcon className="w-8 h-8 text-luxury-gold" />
                </div>
                <h3 className="mb-2 font-semibold text-white">
                  Expert Service
                </h3>
                <p className="text-sm text-white/80">
                  Personalized guidance from real estate professionals
                </p>
              </div>

              <div className="p-6 border rounded-lg border-white/20 bg-white/10 backdrop-blur-sm">
                <div className="flex justify-center mb-4">
                  <TrophyIcon className="w-8 h-8 text-luxury-gold" />
                </div>
                <h3 className="mb-2 font-semibold text-white">Luxury Focus</h3>
                <p className="text-sm text-white/80">
                  Curated selection of high-end rental properties
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="max-w-lg p-8 mx-auto border rounded-xl border-white/20 bg-white/10 backdrop-blur-sm">
              <h2 className="flex items-center justify-center mb-6 text-2xl font-bold text-white">
                <ChatIcon className="w-6 h-6 mr-2 text-luxury-gold" />
                Get In Touch
              </h2>

              <div className="space-y-4 text-white/90">
                  <div className="text-center">
                    <p className="mb-2 font-bold text-white">
                      {displayContactInfo.companyName || 'Assets Infinity'}
                    </p>
                    <p className="text-sm">
                      {displayContactInfo.description ||
                        'Your trusted partner in Bangkok real estate'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                    {/* Email */}
                    {displayContactInfo.email &&
                      displayContactInfo.email.length > 0 && (
                        <div className="text-center">
                          <p className="mb-1 font-medium">Email</p>
                          <a
                            href={`mailto:${displayContactInfo.email[0]}`}
                            className="text-white transition-colors hover:text-luxury-gold-light"
                          >
                            {displayContactInfo.email[0]}
                          </a>
                        </div>
                      )}

                    {/* Phone */}
                    {displayContactInfo.phone &&
                      displayContactInfo.phone.length > 0 && (
                        <div className="text-center">
                          <p className="mb-1 font-medium">Phone</p>
                          <a
                            href={`tel:${displayContactInfo.phone[0].replace(/\D/g, '')}`}
                            className="text-white transition-colors hover:text-luxury-gold-light"
                          >
                            {displayContactInfo.phone[0]}
                          </a>
                        </div>
                      )}
                  </div>

                  {/* LINE ID */}
                  {displayContactInfo.lineId && (
                    <div className="pt-2 text-center">
                      <p className="text-xs text-white/70">
                        LINE ID:{' '}
                        <span className="text-white">
                          {displayContactInfo.lineId}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
            </div>

            {/* Status Message */}
            <div className="mt-12 text-center">
              <p className="text-sm text-white/80">
                We'll be back online soon. Thank you for your patience.
              </p>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute w-20 h-20 rounded-full left-10 top-20 animate-pulse bg-luxury-gold/20 blur-xl"></div>
        <div className="absolute w-16 h-16 delay-1000 rounded-full bottom-32 right-16 animate-pulse bg-white/10 blur-lg"></div>
        <div className="absolute w-12 h-12 delay-500 rounded-full left-4 top-1/2 animate-pulse bg-luxury-gold/30 blur-lg"></div>
      </div>
    </div>
  )
}
